import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { Message } from "@cabinet/charte";
import {
  ecrituresApi,
  ErreurApi,
  planDuDossierApi,
  type Compte,
  type ExerciceVue,
  type JournalVue,
  type PieceVue,
  type SaisieDePiece,
  type Sens,
} from "../api/client";
import {
  analyserMontant,
  celluleSuivante,
  equilibrerLaLigne,
  estPrete,
  formaterTaux,
  ligneVide,
  nouvelleLigne,
  rattacherLaTva,
  rechercherDesComptes,
  synchroniserLaTva,
  totaux,
  type BaremeApplicable,
  type Cellule,
  type Colonne,
  type LigneDeGrille,
} from "../domaine/grille";
import { formaterMontant } from "../domaine/formats";

/*
 * Grille de saisie pilotable au clavier (EF-0402, ENF-33), avec calcul
 * automatique de la TVA (EF-0405).
 *
 * Le clavier suffit à tout : Entrée avance de cellule en cellule et ouvre une
 * ligne au bout de la dernière — pré-remplie de ce qui équilibre la pièce —,
 * les flèches changent de ligne, « = » dans le montant solde la pièce,
 * Ctrl+Entrée enregistre, Ctrl+Maj+Entrée enregistre et valide, Échap ferme.
 * Le compte se retrouve en trois lettres ou en quelques chiffres, dans le plan
 * chargé une fois pour la session ; le tiers se retrouve parmi ceux que le
 * dossier connaît déjà.
 *
 * Une base qui porte un code de taux appelle d'elle-même sa ligne de taxe,
 * sur le compte que le référentiel lui réserve, au taux du barème à la date
 * de la pièce. La ligne de taxe se recalcule quand la base change, et se
 * corrige à la main d'un centime de facture (RG-14) sans que le calcul ne
 * l'écrase ensuite.
 *
 * Aucune règle comptable n'est décidée ici : l'équilibre, la période,
 * l'imputation et le code de taux restent jugés par le serveur. La grille
 * s'attache seulement à ce qu'une pièce courante se passe en un minimum de
 * frappes.
 */

interface Props {
  dossier: string;
  codeDuReferentiel: string;
  devise: string;
  exercice: ExerciceVue;
  journaux: JournalVue[];
  /** Brouillard en cours de correction, ou `null` pour une pièce nouvelle. */
  piece: PieceVue | null;
  peutValider: boolean;
  /** Appelé après tout enregistrement : les listes et les livres se rafraîchissent. */
  surChangement: () => void;
  surFermeture: () => void;
}

interface EnTete {
  journal: string;
  date: string;
  libelle: string;
  reference: string;
}

type Retour = { type: "succes" | "erreur"; texte: string };

const CHAMPS_DEN_TETE = ["journal", "date", "libelle", "reference"] as const;
type ChampDEnTete = (typeof CHAMPS_DEN_TETE)[number];

function versLaGrille(piece: PieceVue, nouvelId: () => number): LigneDeGrille[] {
  return piece.lignes.map((ligne) => ({
    id: nouvelId(),
    compte: ligne.compte,
    sens: ligne.sens,
    montant: ligne.montant.toFixed(2),
    libelle: ligne.libelle,
    tiers: ligne.tiers ?? "",
    codeDeTva: ligne.codeDeTva ?? "",
    tvaPour: null,
    corrigee: false,
  }));
}

/** Aujourd'hui si l'exercice le couvre, sinon son dernier jour. */
function dateParDefaut(exercice: ExerciceVue): string {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  return aujourdhui >= exercice.debut && aujourdhui <= exercice.fin ? aujourdhui : exercice.fin;
}

const cle = (cellule: Cellule) => `${cellule.ligne}:${cellule.colonne}`;

export function GrilleDeSaisie({
  dossier,
  codeDuReferentiel,
  devise,
  exercice,
  journaux,
  piece,
  peutValider,
  surChangement,
  surFermeture,
}: Props) {
  const idSuivant = useRef(1);
  const nouvelId = () => idSuivant.current++;

  const [enTete, setEnTete] = useState<EnTete>({
    journal: piece?.journal ?? journaux[0]?.code ?? "",
    date: piece?.date ?? dateParDefaut(exercice),
    libelle: piece?.libelle ?? "",
    reference: piece?.reference ?? "",
  });
  const [lignes, setLignes] = useState<LigneDeGrille[]>(() =>
    piece != null ? versLaGrille(piece, nouvelId) : [ligneVide(nouvelId(), "DEBIT"), ligneVide(nouvelId(), "CREDIT")],
  );
  const [retour, setRetour] = useState<Retour | null>(null);
  /** Cellule dont la liste de propositions est ouverte, et proposition en surbrillance. */
  const [ouverture, setOuverture] = useState<{ cellule: Cellule; index: number } | null>(null);
  const [fragmentDeTiers, setFragmentDeTiers] = useState("");

  /* --- Plan, barème et tiers -------------------------------------------- */

  // Le plan est chargé une fois : la recherche se fait ensuite en mémoire,
  // sans attendre le réseau à chaque frappe (ENF-02). C'est celui du
  // dossier — le référentiel augmenté de ses sous-comptes (EF-0204) —, celui
  // qui jugera la pièce.
  const plan = useQuery({
    queryKey: ["plan-dossier", dossier, codeDuReferentiel],
    queryFn: async () => (await planDuDossierApi.plan(dossier)).comptes,
  });
  const comptesParNumero = useMemo(
    () => new Map((plan.data ?? []).map((compte) => [compte.numero, compte] as const)),
    [plan.data],
  );

  // Le barème vaut à la date de la pièce (RG-21) : changer la date le relit.
  const baremeCharge = useQuery({
    queryKey: ["bareme", dossier, enTete.date],
    queryFn: () => ecrituresApi.bareme(dossier, enTete.date),
    enabled: enTete.date !== "",
    placeholderData: keepPreviousData,
  });
  const bareme: BaremeApplicable | null = baremeCharge.data ?? null;

  const tiersConnus = useQuery({
    queryKey: ["tiers", dossier, fragmentDeTiers],
    queryFn: () => ecrituresApi.tiers(dossier, fragmentDeTiers),
    enabled: ouverture?.cellule.colonne === "tiers",
    placeholderData: keepPreviousData,
  });

  /* --- Lignes ----------------------------------------------------------- */

  // Une pièce relue ne porte pas le lien entre une taxe et sa base : il se
  // retrouve une fois, dès que le barème est là, avant toute synchronisation.
  const rattachee = useRef(piece == null);

  /** Toute modification passe par ici : les lignes de taxe suivent leurs bases. */
  function appliquer(transformation: (courantes: LigneDeGrille[]) => LigneDeGrille[]) {
    setLignes((courantes) => {
      const base = !rattachee.current && bareme != null ? rattacherLaTva(courantes, bareme) : courantes;
      if (bareme != null) rattachee.current = true;
      return synchroniserLaTva(transformation(base), bareme, nouvelId);
    });
  }

  // Le barème arrive après le premier rendu, ou change avec la date : les
  // taxes se mettent alors en accord avec lui, sans attendre une frappe.
  useEffect(() => {
    if (bareme == null) return;
    // oxlint-disable-next-line react/set-state-in-effect
    appliquer((courantes) => courantes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bareme]);

  function modifier(indice: number, champ: keyof LigneDeGrille, valeur: string) {
    appliquer((courantes) =>
      courantes.map((ligne, i) => {
        if (i !== indice) return ligne;
        // Toucher au montant d'une ligne de taxe, c'est la corriger : le
        // calcul ne l'écrase plus, tant que la base ne l'emmène pas trop loin.
        const corrigee = champ === "montant" && ligne.tvaPour != null ? true : ligne.corrigee;
        return { ...ligne, [champ]: valeur, corrigee };
      }),
    );
  }

  /** Le compte tapé ou choisi apporte le code de taux qu'il porte d'office. */
  function modifierLeCompte(indice: number, numero: string) {
    const connu = comptesParNumero.get(numero.trim());
    appliquer((courantes) =>
      courantes.map((ligne, i) =>
        i === indice
          ? { ...ligne, compte: numero, codeDeTva: connu != null ? (connu.codeDeTva ?? "") : ligne.codeDeTva }
          : ligne,
      ),
    );
  }

  function ajouterUneLigne() {
    setLignes((courantes) => {
      const resultat = synchroniserLaTva(
        [...courantes, nouvelleLigne(courantes, enTete.libelle, nouvelId())],
        bareme,
        nouvelId,
      );
      aFocaliser.current = { ligne: resultat.length - 1, colonne: "compte" };
      return resultat;
    });
  }

  function retirerLaLigne(indice: number) {
    const ligne = lignes[indice];
    if (ligne == null) return;
    setOuverture(null);
    if (ligne.tvaPour != null) {
      // Une ligne de taxe ne se retire pas : c'est sa base qui cesse d'en
      // appeler une, en perdant son code.
      const base = ligne.tvaPour;
      appliquer((courantes) => courantes.map((l) => (l.id === base ? { ...l, codeDeTva: "" } : l)));
      return;
    }
    if (lignes.length <= 2) return;
    appliquer((courantes) => courantes.filter((_, i) => i !== indice));
    aFocaliser.current = { ligne: Math.min(indice, lignes.length - 2), colonne: "compte" };
  }

  /* --- Focalisation ----------------------------------------------------- */

  const champsDEnTete = useRef(new Map<ChampDEnTete, HTMLInputElement | HTMLSelectElement>());
  const cellules = useRef(new Map<string, HTMLInputElement | HTMLSelectElement>());
  const boutonEnregistrer = useRef<HTMLButtonElement>(null);
  const aFocaliser = useRef<Cellule | ChampDEnTete | null>(null);

  const referenceDe = (cellule: Cellule) => (element: HTMLInputElement | HTMLSelectElement | null) => {
    if (element == null) cellules.current.delete(cle(cellule));
    else cellules.current.set(cle(cellule), element);
  };
  const referenceDEnTete = (champ: ChampDEnTete) => (element: HTMLInputElement | HTMLSelectElement | null) => {
    if (element == null) champsDEnTete.current.delete(champ);
    else champsDEnTete.current.set(champ, element);
  };

  function focaliser(cible: Cellule | ChampDEnTete): boolean {
    const element =
      typeof cible === "string" ? champsDEnTete.current.get(cible) : cellules.current.get(cle(cible));
    if (element == null) return false;
    element.focus();
    if (element instanceof HTMLInputElement && element.type !== "date") element.select();
    return true;
  }

  // Une cellule qui n'existe pas encore — celle d'une ligne qu'on vient
  // d'ajouter — se focalise après le rendu qui la fait naître.
  useEffect(() => {
    if (aFocaliser.current == null) return;
    focaliser(aFocaliser.current);
    aFocaliser.current = null;
  });

  useEffect(() => {
    focaliser(piece == null ? "libelle" : { ligne: 0, colonne: "compte" });
    // Au premier rendu seulement : ensuite, c'est la frappe qui déplace le focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cellule suivante dans la frappe. Une ligne de taxe se saute d'un bloc :
   * elle se relit et se corrige avec les flèches, pas dans le fil de la
   * saisie. Au bout de la dernière ligne, une pièce prête attend son
   * enregistrement, une pièce incomplète appelle une ligne de plus.
   */
  function avancer(cellule: Cellule) {
    let suivante = celluleSuivante(cellule, lignes.length);
    while (suivante != null) {
      if (suivante.colonne === "compte" && lignes[suivante.ligne]?.tvaPour != null) {
        suivante = celluleSuivante({ ligne: suivante.ligne, colonne: "tiers" }, lignes.length);
        continue;
      }
      if (focaliser(suivante)) return;
      suivante = celluleSuivante(suivante, lignes.length);
    }
    if (estPrete(lignes)) boutonEnregistrer.current?.focus();
    else ajouterUneLigne();
  }

  /** Même colonne sur la ligne voisine ; sur une ligne de taxe, son montant. */
  function changerDeLigne(cellule: Cellule, pas: 1 | -1) {
    const ligne = cellule.ligne + pas;
    if (ligne < 0 || ligne >= lignes.length) return;
    if (focaliser({ ligne, colonne: cellule.colonne })) return;
    if (focaliser({ ligne, colonne: "montant" })) return;
    focaliser({ ligne, colonne: "libelle" });
  }

  function choisirLeCompte(cellule: Cellule, compte: Compte) {
    modifierLeCompte(cellule.ligne, compte.numero);
    setOuverture(null);
  }

  function choisirLeTiers(cellule: Cellule, tiers: string) {
    modifier(cellule.ligne, "tiers", tiers);
    setOuverture(null);
  }

  function choisirLeJournal(code: string) {
    setEnTete((courant) => ({ ...courant, journal: code }));
    // La contrepartie du journal est proposée sur la dernière ligne encore
    // vide : le 4411 sur un achat, le 3421 sur une vente.
    const contrepartie = journaux.find((j) => j.code === code)?.compteDeContrepartie;
    if (contrepartie == null) return;
    appliquer((courantes) => {
      const derniere = courantes.length - 1;
      if (derniere < 0 || courantes[derniere].compte !== "" || courantes[derniere].tvaPour != null) return courantes;
      return courantes.map((ligne, i) => (i === derniere ? { ...ligne, compte: contrepartie } : ligne));
    });
  }

  /* --- Propositions ----------------------------------------------------- */

  const celluleOuverte = ouverture?.cellule ?? null;
  const propositionsDeComptes: Compte[] = useMemo(() => {
    if (celluleOuverte?.colonne !== "compte") return [];
    return rechercherDesComptes(plan.data ?? [], lignes[celluleOuverte.ligne]?.compte ?? "");
  }, [celluleOuverte, lignes, plan.data]);
  const propositionsDeTiers: string[] = useMemo(() => {
    if (celluleOuverte?.colonne !== "tiers") return [];
    const tape = (lignes[celluleOuverte.ligne]?.tiers ?? "").trim();
    return (tiersConnus.data ?? []).filter((tiers) => tiers !== tape);
  }, [celluleOuverte, lignes, tiersConnus.data]);

  /* --- Clavier ---------------------------------------------------------- */

  function surToucheDeCellule(evenement: React.KeyboardEvent, cellule: Cellule) {
    const listeOuverte = ouverture != null && cle(ouverture.cellule) === cle(cellule);
    const propositions: readonly unknown[] =
      cellule.colonne === "compte" ? propositionsDeComptes : cellule.colonne === "tiers" ? propositionsDeTiers : [];
    const choisir = (index: number) => {
      if (cellule.colonne === "compte") choisirLeCompte(cellule, propositionsDeComptes[index]);
      else choisirLeTiers(cellule, propositionsDeTiers[index]);
    };

    switch (evenement.key) {
      case "ArrowDown":
      case "ArrowUp": {
        const pas = evenement.key === "ArrowDown" ? 1 : -1;
        evenement.preventDefault();
        if (listeOuverte && propositions.length > 0) {
          setOuverture((courante) =>
            courante == null
              ? courante
              : { ...courante, index: (courante.index + pas + propositions.length) % propositions.length },
          );
          return;
        }
        changerDeLigne(cellule, pas);
        return;
      }
      case "Enter": {
        evenement.preventDefault();
        if (listeOuverte && propositions.length > 0 && ouverture != null) choisir(ouverture.index);
        avancer(cellule);
        return;
      }
      case "Tab": {
        // La tabulation garde son sens natif ; elle retient seulement la
        // proposition en surbrillance avant de partir.
        if (listeOuverte && propositions.length > 0 && ouverture != null) choisir(ouverture.index);
        return;
      }
      case "Escape": {
        if (listeOuverte) {
          evenement.preventDefault();
          evenement.stopPropagation();
          setOuverture(null);
        }
        return;
      }
      case "Delete":
      case "Backspace": {
        if (evenement.ctrlKey || evenement.metaKey) {
          evenement.preventDefault();
          retirerLaLigne(cellule.ligne);
        }
        return;
      }
      case "=":
      case "*": {
        if (cellule.colonne === "montant") {
          evenement.preventDefault();
          appliquer((courantes) => equilibrerLaLigne(courantes, cellule.ligne));
        }
        return;
      }
      default:
        return;
    }
  }

  function surToucheDEnTete(evenement: React.KeyboardEvent, champ: ChampDEnTete) {
    if (evenement.key !== "Enter") return;
    evenement.preventDefault();
    const indice = CHAMPS_DEN_TETE.indexOf(champ);
    if (indice < CHAMPS_DEN_TETE.length - 1) focaliser(CHAMPS_DEN_TETE[indice + 1]);
    else focaliser({ ligne: 0, colonne: "compte" });
  }

  function surToucheDuFormulaire(evenement: React.KeyboardEvent) {
    if (evenement.key === "Enter" && (evenement.ctrlKey || evenement.metaKey)) {
      evenement.preventDefault();
      if (evenement.shiftKey) {
        if (peutValider) enregistrer(true);
      } else {
        enregistrer(false);
      }
      return;
    }
    if (evenement.key === "Escape" && !evenement.defaultPrevented) {
      evenement.preventDefault();
      fermer();
    }
  }

  /* --- Enregistrement --------------------------------------------------- */

  const bilan = totaux(lignes);
  const taxes = totaux(lignes.filter((ligne) => ligne.tvaPour != null));
  const prete = estPrete(lignes);
  const modifiee =
    enTete.libelle.trim() !== "" || lignes.some((ligne) => ligne.compte !== "" || ligne.montant !== "");

  const envoi = useMutation({
    mutationFn: async ({ saisie, valider }: { saisie: SaisieDePiece; valider: boolean }) => {
      const enregistree =
        piece == null ? await ecrituresApi.saisir(dossier, saisie) : await ecrituresApi.corriger(piece.id, saisie);
      return valider ? ecrituresApi.valider(enregistree.id) : enregistree;
    },
    onSuccess: (resultat) => {
      surChangement();
      if (piece != null) {
        surFermeture();
        return;
      }
      // Saisie en rafale : la pièce suivante garde le journal et la date,
      // et la frappe reprend au libellé sans passer par la souris.
      setRetour({
        type: "succes",
        texte:
          resultat.statut === "VALIDEE"
            ? `Pièce validée sous le numéro ${resultat.numero} du journal ${resultat.journal} — ${formaterMontant(resultat.totalDebit)} ${resultat.devise}.`
            : `Pièce enregistrée en brouillard — ${formaterMontant(resultat.totalDebit)} ${resultat.devise}. Suivante ?`,
      });
      setEnTete((courant) => ({ ...courant, libelle: "", reference: "" }));
      setLignes([ligneVide(nouvelId(), "DEBIT"), ligneVide(nouvelId(), "CREDIT")]);
      setOuverture(null);
      aFocaliser.current = "libelle";
    },
    onError: (echec) =>
      setRetour({
        type: "erreur",
        texte: echec instanceof ErreurApi ? echec.message : "L'enregistrement n'a pas abouti.",
      }),
  });

  function enregistrer(valider: boolean) {
    if (!prete || envoi.isPending) return;
    setRetour(null);
    envoi.mutate({
      valider,
      saisie: {
        journal: enTete.journal,
        date: enTete.date,
        libelle: enTete.libelle.trim(),
        reference: enTete.reference.trim() || null,
        lignes: lignes.map((ligne) => ({
          compte: ligne.compte.trim(),
          sens: ligne.sens,
          montant: analyserMontant(ligne.montant) ?? 0,
          libelle: ligne.libelle.trim() || enTete.libelle.trim(),
          tiers: ligne.tiers.trim() || null,
          codeDeTva: ligne.codeDeTva || null,
        })),
      },
    });
  }

  function fermer() {
    if (modifiee && !window.confirm("Abandonner la saisie en cours ?")) return;
    surFermeture();
  }

  /* --- Rendu ------------------------------------------------------------ */

  const journalCourant = journaux.find((j) => j.code === enTete.journal);
  const tauxParCode = new Map((bareme?.taux ?? []).map((taux) => [taux.code, taux] as const));

  return (
    <form
      className="carte saisie"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        enregistrer(false);
      }}
      onKeyDown={surToucheDuFormulaire}
    >
      <div className="barre-actions">
        <h2 style={{ margin: 0 }}>{piece == null ? "Nouvelle pièce" : "Correction du brouillard"}</h2>
        <span className="espace" />
        <span className="discret-texte">
          Exercice {exercice.libelle}
          {journalCourant?.compteDeContrepartie != null && ` · contrepartie ${journalCourant.compteDeContrepartie}`}
          {bareme != null && ` · TVA ${bareme.taux.map((t) => formaterTaux(t.pourCent)).join(", ")}`}
        </span>
      </div>

      {retour != null && (
        <Message type={retour.type}>
          <span aria-live="polite">{retour.texte}</span>
        </Message>
      )}

      <div className="champs">
        <div className="champ">
          <label htmlFor="journal">Journal</label>
          <select
            id="journal"
            ref={referenceDEnTete("journal")}
            value={enTete.journal}
            onChange={(e) => choisirLeJournal(e.target.value)}
            onKeyDown={(e) => surToucheDEnTete(e, "journal")}
            required
          >
            {journaux.map((journal) => (
              <option key={journal.code} value={journal.code}>
                {journal.code} — {journal.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            ref={referenceDEnTete("date")}
            value={enTete.date}
            min={exercice.debut}
            max={exercice.fin}
            onChange={(e) => setEnTete((courant) => ({ ...courant, date: e.target.value }))}
            onKeyDown={(e) => surToucheDEnTete(e, "date")}
            required
          />
        </div>
        <div className="champ">
          <label htmlFor="libelle">Libellé</label>
          <input
            id="libelle"
            ref={referenceDEnTete("libelle")}
            value={enTete.libelle}
            onChange={(e) => setEnTete((courant) => ({ ...courant, libelle: e.target.value }))}
            onKeyDown={(e) => surToucheDEnTete(e, "libelle")}
            placeholder="Facture ATLAS n° 2026-114"
            required
          />
        </div>
        <div className="champ">
          <label htmlFor="reference">Référence</label>
          <input
            id="reference"
            ref={referenceDEnTete("reference")}
            value={enTete.reference}
            onChange={(e) => setEnTete((courant) => ({ ...courant, reference: e.target.value }))}
            onKeyDown={(e) => surToucheDEnTete(e, "reference")}
            placeholder="N° de facture, de relevé…"
          />
        </div>
      </div>

      <div className="tableau-conteneur">
        <table className="grille-saisie">
          <thead>
            <tr>
              <th>Compte</th>
              <th>Sens</th>
              <th className="nombre">Montant ({devise})</th>
              <th>TVA</th>
              <th>Libellé de ligne</th>
              <th>Tiers</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.map((ligne, indice) => {
              const compteChoisi = comptesParNumero.get(ligne.compte.trim());
              const cellule = (colonne: Colonne): Cellule => ({ ligne: indice, colonne });
              const ouverteSur = (colonne: Colonne) =>
                ouverture != null && ouverture.cellule.ligne === indice && ouverture.cellule.colonne === colonne;
              const taxe = ligne.tvaPour != null;
              return (
                <tr key={ligne.id} className={taxe ? "ligne-de-taxe" : undefined}>
                  <td className="cellule-a-propositions">
                    {taxe ? (
                      <span className="numero-compte">{ligne.compte}</span>
                    ) : (
                      <input
                        ref={referenceDe(cellule("compte"))}
                        value={ligne.compte}
                        onChange={(e) => {
                          modifierLeCompte(indice, e.target.value);
                          setOuverture({ cellule: cellule("compte"), index: 0 });
                        }}
                        onKeyDown={(e) => surToucheDeCellule(e, cellule("compte"))}
                        onBlur={() => ouverteSur("compte") && setOuverture(null)}
                        placeholder="4411 ou fou…"
                        aria-label={`Compte de la ligne ${indice + 1}`}
                        aria-autocomplete="list"
                        aria-expanded={ouverteSur("compte") && propositionsDeComptes.length > 0}
                        autoComplete="off"
                        required
                      />
                    )}
                    {compteChoisi != null && (
                      <div className="aide-cellule" title={compteChoisi.libelle}>
                        {compteChoisi.libelle}
                        {compteChoisi.collectif && " · tiers exigé"}
                      </div>
                    )}
                    {ouverteSur("compte") && propositionsDeComptes.length > 0 && ouverture != null && (
                      <Propositions
                        elements={propositionsDeComptes}
                        index={ouverture.index}
                        surChoix={(compte) => {
                          choisirLeCompte(cellule("compte"), compte);
                          focaliser(cellule("sens"));
                        }}
                        rendre={(compte) => (
                          <>
                            <span className="numero">{compte.numero}</span>
                            <span>{compte.libelle}</span>
                            {compte.collectif && <span className="discret-texte">tiers exigé</span>}
                            {compte.codeDeTva != null && <span className="discret-texte">TVA {compte.codeDeTva}</span>}
                          </>
                        )}
                      />
                    )}
                  </td>
                  <td>
                    {taxe ? (
                      <span className="discret-texte">{ligne.sens === "DEBIT" ? "Débit" : "Crédit"}</span>
                    ) : (
                      <select
                        ref={referenceDe(cellule("sens"))}
                        value={ligne.sens}
                        onChange={(e) => modifier(indice, "sens", e.target.value as Sens)}
                        onKeyDown={(e) => surToucheDeCellule(e, cellule("sens"))}
                        aria-label={`Sens de la ligne ${indice + 1}`}
                      >
                        <option value="DEBIT">Débit</option>
                        <option value="CREDIT">Crédit</option>
                      </select>
                    )}
                  </td>
                  <td>
                    <input
                      ref={referenceDe(cellule("montant"))}
                      inputMode="decimal"
                      className={`nombre${analyserMontant(ligne.montant) == null ? " invalide" : ""}`}
                      value={ligne.montant}
                      onChange={(e) => modifier(indice, "montant", e.target.value)}
                      onKeyDown={(e) => surToucheDeCellule(e, cellule("montant"))}
                      placeholder="0,00"
                      aria-label={taxe ? `Taxe de la ligne ${indice + 1}` : `Montant de la ligne ${indice + 1}`}
                      autoComplete="off"
                      required
                    />
                  </td>
                  <td>
                    {taxe ? (
                      <span className="discret-texte" title={ligne.corrigee ? "Montant corrigé à la main" : "Calculée d'après la base"}>
                        {ligne.corrigee ? "corrigée" : "calculée"} · {ligne.codeDeTva}
                      </span>
                    ) : (
                      <select
                        ref={referenceDe(cellule("tva"))}
                        value={ligne.codeDeTva}
                        onChange={(e) => modifier(indice, "codeDeTva", e.target.value)}
                        onKeyDown={(e) => surToucheDeCellule(e, cellule("tva"))}
                        aria-label={`Taux de TVA de la ligne ${indice + 1}`}
                        disabled={bareme == null}
                      >
                        <option value="">—</option>
                        {(bareme?.taux ?? []).map((taux) => (
                          <option key={taux.code} value={taux.code}>
                            {taux.code} · {formaterTaux(taux.pourCent)}
                          </option>
                        ))}
                        {ligne.codeDeTva !== "" && !tauxParCode.has(ligne.codeDeTva) && (
                          <option value={ligne.codeDeTva}>{ligne.codeDeTva} · hors barème</option>
                        )}
                      </select>
                    )}
                  </td>
                  <td>
                    <input
                      ref={referenceDe(cellule("libelle"))}
                      value={ligne.libelle}
                      onChange={(e) => modifier(indice, "libelle", e.target.value)}
                      onKeyDown={(e) => surToucheDeCellule(e, cellule("libelle"))}
                      onFocus={() => {
                        // Un libellé vide reprend celui de la pièce : la frappe
                        // passe alors d'une seule touche.
                        if (ligne.libelle === "" && enTete.libelle.trim() !== "") {
                          modifier(indice, "libelle", enTete.libelle.trim());
                        }
                      }}
                      placeholder="Reprend celui de la pièce"
                      aria-label={`Libellé de la ligne ${indice + 1}`}
                      autoComplete="off"
                    />
                  </td>
                  <td className="cellule-a-propositions">
                    {!taxe && (
                      <input
                        ref={referenceDe(cellule("tiers"))}
                        value={ligne.tiers}
                        onChange={(e) => {
                          modifier(indice, "tiers", e.target.value);
                          setFragmentDeTiers(e.target.value.trim());
                          setOuverture({ cellule: cellule("tiers"), index: 0 });
                        }}
                        onFocus={() => {
                          setFragmentDeTiers(ligne.tiers.trim());
                          setOuverture({ cellule: cellule("tiers"), index: 0 });
                        }}
                        onKeyDown={(e) => surToucheDeCellule(e, cellule("tiers"))}
                        onBlur={() => ouverteSur("tiers") && setOuverture(null)}
                        placeholder={compteChoisi?.collectif ? "Exigé" : "—"}
                        aria-label={`Tiers de la ligne ${indice + 1}`}
                        aria-autocomplete="list"
                        aria-expanded={ouverteSur("tiers") && propositionsDeTiers.length > 0}
                        autoComplete="off"
                        required={compteChoisi?.collectif === true}
                      />
                    )}
                    {ouverteSur("tiers") && propositionsDeTiers.length > 0 && ouverture != null && (
                      <Propositions
                        elements={propositionsDeTiers}
                        index={ouverture.index}
                        surChoix={(tiers) => choisirLeTiers(cellule("tiers"), tiers)}
                        rendre={(tiers) => <span>{tiers}</span>}
                      />
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="tertiaire"
                      tabIndex={-1}
                      disabled={!taxe && lignes.length <= 2}
                      onClick={() => retirerLaLigne(indice)}
                      aria-label={taxe ? `Retirer la taxe de la ligne ${indice + 1}` : `Retirer la ligne ${indice + 1}`}
                      title={taxe ? "Retire le code de taux de la base" : "Ctrl+Suppr"}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total">
              <td colSpan={2}>Totaux</td>
              <td className="nombre">
                {formaterMontant(bilan.debit / 100)} / {formaterMontant(bilan.credit / 100)}
              </td>
              <td colSpan={4} className={bilan.ecart === 0 && bilan.montantsLisibles ? "discret-texte" : "ecart"}>
                {!bilan.montantsLisibles
                  ? "Un montant n'est pas lisible : deux décimales au plus."
                  : bilan.ecart === 0
                    ? bilan.debit === 0
                      ? "Pièce vide"
                      : `Pièce équilibrée${taxes.debit + taxes.credit > 0 ? ` · dont TVA ${formaterMontant((taxes.debit + taxes.credit) / 100)}` : ""}`
                    : `Écart : ${formaterMontant(bilan.ecart / 100)} — « = » dans un montant le solde`}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="barre-actions">
        <button type="button" className="tertiaire" onClick={ajouterUneLigne} title="Entrée au bout de la dernière ligne">
          Ajouter une ligne
        </button>
        <span className="espace" />
        <button type="button" onClick={fermer} title="Échap">
          Annuler
        </button>
        <button ref={boutonEnregistrer} type="submit" className="principal" disabled={!prete || envoi.isPending} title="Ctrl+Entrée">
          {piece == null ? "Enregistrer en brouillard" : "Enregistrer la correction"}
        </button>
        {peutValider && (
          <button
            type="button"
            className="principal"
            disabled={!prete || envoi.isPending}
            onClick={() => enregistrer(true)}
            title="Ctrl+Maj+Entrée"
          >
            Enregistrer et valider
          </button>
        )}
      </div>

      <p className="discret-texte aide-clavier">
        <kbd>Entrée</kbd> cellule suivante, puis nouvelle ligne équilibrante · <kbd>↑</kbd> <kbd>↓</kbd> ligne ·{" "}
        <kbd>d</kbd> <kbd>c</kbd> dans le sens · <kbd>=</kbd> dans le montant solde la pièce ·{" "}
        <kbd>Ctrl</kbd>+<kbd>Suppr</kbd> retire la ligne ·{" "}
        <kbd>Ctrl</kbd>+<kbd>Entrée</kbd> enregistre
        {peutValider && (
          <>
            {" "}
            · <kbd>Ctrl</kbd>+<kbd>Maj</kbd>+<kbd>Entrée</kbd> enregistre et valide
          </>
        )}{" "}
        · <kbd>Échap</kbd> ferme. Une base à un taux appelle sa ligne de taxe ; son montant se corrige à la main.
      </p>
    </form>
  );
}

/**
 * Liste de propositions sous une cellule.
 *
 * Le choix à la souris passe par « mousedown » et non « click » : au clic, la
 * cellule aurait déjà perdu le focus et la liste serait déjà refermée.
 */
function Propositions<T>({
  elements,
  index,
  surChoix,
  rendre,
}: {
  elements: readonly T[];
  index: number;
  surChoix: (element: T) => void;
  rendre: (element: T) => React.ReactNode;
}) {
  return (
    <ul className="propositions" role="listbox">
      {elements.map((element, i) => (
        <li
          key={i}
          role="option"
          aria-selected={i === index}
          className={i === index ? "active" : undefined}
          onMouseDown={(evenement) => {
            evenement.preventDefault();
            surChoix(element);
          }}
        >
          {rendre(element)}
        </li>
      ))}
    </ul>
  );
}
